/**
 * A voice or video call.
 *
 * Only signalling is stored. The audio and video streams are peer-to-peer and
 * never pass through our backend.
 *
 * @typedef {'ringing'|'connecting'|'connected'|'ended'|'declined'|'missed'|'failed'} CallStatus
 *
 * @typedef {object} Call
 * @property {string}      id
 * @property {string}      chatId
 * @property {string}      callerId
 * @property {string[]}    calleeIds
 * @property {'voice'|'video'} type
 * @property {CallStatus}  status
 * @property {Date}        createdAt
 * @property {Date|null}   answeredAt
 * @property {Date|null}   endedAt
 * @property {number}      durationMs
 */

import { toDate } from '../utils/time.js'

export function toCall(id, raw = {}) {
  const answeredAt = toDate(raw.answeredAt ?? raw.answered_at)
  const endedAt    = toDate(raw.endedAt ?? raw.ended_at)
  return {
    id,
    chatId:     raw.chatId ?? raw.chat_id ?? '',
    callerId:   raw.callerId ?? raw.caller_id ?? '',
    calleeIds:  raw.calleeIds ?? raw.callee_ids ?? [],
    type:       raw.type ?? 'voice',
    status:     raw.status ?? 'ringing',
    createdAt:  toDate(raw.createdAt ?? raw.created_at) ?? new Date(),
    answeredAt,
    endedAt,
    durationMs: answeredAt && endedAt ? endedAt.getTime() - answeredAt.getTime() : 0
  }
}

export function isIncoming(call, currentUserId) {
  return call.callerId !== currentUserId
}

export function wasAnswered(call) {
  return call.answeredAt !== null
}

/**
 * Public STUN only. This is the default until a TURN provider is chosen -
 * see the warning in services/contracts/calls.js. Roughly 10-20% of calls
 * will fail to connect on restrictive networks until TURN is added here.
 */
export const DEFAULT_ICE_CONFIG = {
  iceServers: [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] }
  ]
}
